import React, { useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { useOneOnOneInsightsFiltersData } from 'screens/oneOnOneRabbit/Insights/slice/OneOnOneInsightsFiltersSlice';
import { ArchiveMeetingModal } from 'screens/oneOnOneRabbit/InstancePage/common/modals/ArchiveMeetingModal';
import { openCreateOneOnOneDrawer } from 'screens/oneOnOneRabbit/creationDrawer/slice/CreateOneOnOneDrawerSlice';
import { getOneOnOneInstancePagePath } from 'screens/oneOnOneRabbit/helpers';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { Plus } from 'assets/assetsV1/iconComponents';
import NoOneOnOne from 'assets/assetsV1/illustrations/newIllustrations/noOneOnOne.svg';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { getPersistenceKey } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import {
  sendAgGridFilterEvent,
  sendArchivedOneonone,
  sendClickedOneonOneInstancePageOpened,
} from 'constants/mixpanelHelper';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Button } from 'uiToolkitV1/Button';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { Paper } from 'uiToolkitV1/Paper';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';

import { oneOnOneLandingPageReactQueryKeys } from '../../constants/oneOnOneReactQueryKeys';
import { useOneOnOneLandingPageDetails } from '../../hooks/dataHooks';
import EmployeeData from '../Components/ActiveOneOnOneComponents/EmployeeData';
import { oneOnOneLandingPageColumnDefs } from './columnDefs';
import './styles.css';
import { generateActiveOneOnOneMenuOptions } from './utils';

const ActiveOneOnOneTable = ({ gridRef }) => {
  const { dateRange, filterText } = useOneOnOneInsightsFiltersData();
  const selectedCohort = useSelectedView();
  const [shouldRenderGrid, setShouldRenderGrid] = useState(false);

  const modules = useRouteToNavLevels();
  const isShowReportees = modules?.l1Module === 'team';

  // When selectedCohort.id = -9, it signifies it's my 1-1 page.
  // When the Active 1-1 table renders from the My 1-1 page, we will set selectedCohort.id to null.
  // However, when the Active 1-1 table renders from My team pages direct/skip team, we want to pass the cohort id.

  const selectedCohortToPass = selectedCohort?.id === -9 ? null : selectedCohort?.id;

  const { data, isLoading } = useOneOnOneLandingPageDetails({
    isArchivedList: false,
    selectedCohortId: selectedCohortToPass,
    dateRange,
    isShowReportees,
  });

  const [isArchivalDrawerOpen, setIsArchivalDrawerOpen] = useState(false);

  const closeArchivalDrawer = () => {
    setIsArchivalDrawerOpen(false);
  };

  const history = useHistory();

  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const toast = useToast();

  const [displayName, setDisplayName] = useState('');
  const [masterId, setMasterId] = useState('');

  const columnDefs = useMemo(
    () =>
      data &&
      Object.keys(data?.columns)?.map((column) =>
        oneOnOneLandingPageColumnDefs[column]({
          displayName: column,
          id: column,
        })
      ),

    [data]
  );

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      sortable: true,
      menuTabs: ['filterMenuTab'],
      headerComponent: CustomHeaderCell,
    }),
    []
  );

  const responsiveColumnDef = useMemo(
    () => [
      {
        colId: 'employee',
        field: 'employee',
        id: 'employee',
        sortable: true,
        minWidth: 255,
        flex: 1,
        suppressHeaderMenuButton: true,
        headerName: 'Name',
        cellRenderer: EmployeeData,
        valueGetter: (params) => params?.data?.employee?.value?.employeeMiniProfile?.displayName,
        filterOptions: ['contains', 'notContains'],
        getQuickFilterText: (params) => params?.data?.employee?.value?.employeeMiniProfile?.displayName,
        filterParams: {
          applyMiniFilterWhileTyping: true,
        },
      },
    ],
    []
  );

  const autoGroupColumnDefResponsive = {
    colId: 'employee',
    field: 'employee',
    id: 'employee',
    flex: 1,
    cellStyle: { height: 'auto' },
    wrapText: true,
    autoHeight: true,
    menuTabs: [],
  };

  const openCreateOneOnOneSidepanel = ({ employeeId = null, employeeMiniProfile }) => {
    dispatch(
      openCreateOneOnOneDrawer({
        excludedParticipantIds: [],
        employeeId,
        employeeMiniProfile,
      })
    );
  };

  const addNewOneonOneButton = () => (
    <Button onClick={openCreateOneOnOneSidepanel} StartIcon={Plus}>
      Add New
    </Button>
  );

  // eslint-disable-next-line no-shadow
  const rowClickHandler = ({ data, column }) => {
    if (column.colId === 'kebab-menu') {
      //  To prevent event bubbling.
    } else {
      // eslint-disable-next-line no-shadow
      const masterId = data?.employee?.value?.oneOnOneMasterId;
      const instanceId = data?.nextOneOnOne?.value?.latestInstanceId;

      // if masterId or instanceId are numbers, then only redirect
      // we can't use !isNaN here because incase of null i.e !isNaN(null) it will return true
      const oneOnOneInstanceExists = typeof masterId === 'number' && typeof instanceId === 'number';

      if (!oneOnOneInstanceExists) {
        openCreateOneOnOneSidepanel({
          employeeId: data?.employee?.value?.employeeMiniProfile?.id,
          employeeMiniProfile: data?.employee?.value?.employeeMiniProfile,
        });
        return;
      }
      if (oneOnOneInstanceExists) {
        const route = getOneOnOneInstancePagePath({
          masterId,
          instanceId,
          selectedCohort: selectedCohort?.id,
        });
        history.push(route);
        sendClickedOneonOneInstancePageOpened({
          eventProperties: {
            source: 'Active 1:1s Table',
          },
        });
      }
      // eslint-disable-next-line consistent-return
      return null;
    }
  };

  /* Grid State persistence Key */
  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.ONE_ON_ONE;
    const viewId = 'My_1on1';
    const tabId = 'Explore';
    const uniqueId = 'Active_1on1_Grid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: true,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  const refetchHandler = () => {
    queryClient.invalidateQueries([oneOnOneLandingPageReactQueryKeys.oneOnOneLandingPageDetails]);
    queryClient.invalidateQueries([oneOnOneLandingPageReactQueryKeys.oneOnOneActiveArchiveCount]);
  };

  const onSave = () => {
    try {
      refetchHandler();
      sendArchivedOneonone({
        eventProperties: {
          source: 'My 1:1s',
        },
      });
      closeArchivalDrawer();
      toast({
        title: `Your 1:1 with ${displayName} is archived successfully`,
        status: 'success',
      });
    } catch (error) {
      console.error('Error while archiving 1:1 : ', error);
    }
  };

  useEffect(() => {
    if (selectedCohort?.id) {
      setShouldRenderGrid(true);
    }
  }, [selectedCohort?.id]);

  if (!shouldRenderGrid) {
    return null;
  }

  if (data?.data?.length === 0) {
    return (
      <div style={{ marginTop: '60px' }}>
        <EmptyScreen
          heading="There are no 1:1's scheduled"
          description="Set up 1:1's today to give/receive real-time feedback on things well done or areas needing improvement and align your work with overall company goals."
          illustrationSrc={NoOneOnOne}
          CTAComp={addNewOneonOneButton}
        />
      </div>
    );
  }

  if (isLoading) {
    return <SkeletonLoader />;
  }

  return (
    <>
      <Paper pt="22px" pb="0px">
        <AgGridTable
          ref={gridRef}
          key={gridStatePersistenceKey} // we want to re-initialize on key change
          gridStatePersistenceKey={gridStatePersistenceKey}
          pagination
          suppressPaginationPanel={
            data?.data?.length < 6 || data?.data?.length === undefined || data?.data?.length === 0
          }
          autoGroupColumnDef={isMobileView() && autoGroupColumnDefResponsive}
          /* In Mobile view, we have only one column , so pinning it left is creating empty space in center */
          additionalMobileProps={{ pinned: undefined, lockPosition: undefined }}
          isMobileView={isMobileView()}
          domLayout="autoHeight"
          animateRows={false}
          tooltipShowDelay={0}
          rowData={data?.data}
          columnDefs={isMobileView() ? responsiveColumnDef : columnDefs}
          defaultColDef={defaultColDef}
          style={{ width: '100%', height: '100%' }}
          rowHeight={100}
          rowStyle={{ cursor: 'pointer' }}
          paginationPageSize={isMobileView() ? 4 : 10}
          sortable
          onCellClicked={rowClickHandler}
          onFilterChanged={(event) => {
            sendAgGridFilterEvent({
              eventProperties: {
                columnName: event?.columns[0]?.colDef?.headerName,
                source: 'Active1:1s',
              },
            });
          }}
          cacheQuickFilter
          quickFilterText={filterText}
          sideBar={sideBar}
          kebabMenuIsRowSpecific
          // eslint-disable-next-line no-shadow
          kebabMenuOptionsGenerator={(data) => {
            const options = generateActiveOneOnOneMenuOptions({
              data,
              setIsArchivalDrawerOpen,
              history,
              dispatch,
              setDisplayName,
              setMasterId,
              selectedCohort,
            });
            return options;
          }}
          containerAutoHeight
          paginationPageSizeSelector={isMobileView() ? false : [10, 20, 50]}
        />
      </Paper>
      <ArchiveMeetingModal
        isCentered
        employeeName={displayName}
        isOpen={isArchivalDrawerOpen}
        onClose={closeArchivalDrawer}
        onSave={onSave}
        masterIdFromProp={masterId}
      />
    </>
  );
};

export default ActiveOneOnOneTable;
